import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Group, Stack, Table, Title } from '@mantine/core';
import { Nuts } from '@src/types/maps';
import { useSelectHandler } from '@lib/hooks';
import { CompareItem } from '@generated/graphql';
import { useQuery } from '@apollo/client';
import { GET_COMPARISON } from '@queries/comparison';
import { getRatingDescription } from '@lib/helpers';
import GraphqlError from './graphql-error';
import LoadingData from './graphql-loading-data';
import classes from './nuts-compare-table.module.css';

interface CompareTableProps {
  nutDestinations: Nuts[];
}
function CompareTable({ nutDestinations }: CompareTableProps) {
  const { tripProfileId } = useParams();
  const [selectClicked, setSelectClicked] = useState(false);
  const { selectHandler: selectProcessor } = useSelectHandler();

  const validNutDestinations = nutDestinations
    .filter((d) => typeof d.nutsName === 'string' && d.nutsName !== undefined)
    .map((d) => ({
      ...d,
      nutsName: d.nutsName as string,
    }));

  const { loading, error, data } = useQuery(GET_COMPARISON, {
    variables: {
      destinations: validNutDestinations,
      tripProfileId: tripProfileId || '',
    },
  });
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  const rowsLabels = [
    { key: 'weather', label: 'Weather' },
    { key: 'notableFeatures', label: 'Notable Features' },
    { key: 'transportation', label: 'Transportation' },
    { key: 'infrastructure', label: 'Tourist Infrastructure' },
    { key: 'safety', label: 'Safety' },
    { key: 'crowds', label: 'Crowds' },
  ];

  const rows = rowsLabels.map(({ key, label }) => (
    <Table.Tr key={key} className={classes.row}>
      <Table.Td>{label}</Table.Td>
      {data?.getComparison.comparison.map((item, index) => {
        const value = item[key as keyof CompareItem];

        // Check if the value is a number between 1 and 5, and convert it if so
        const displayValue =
          typeof value === 'number' && value >= 1 && value <= 5 ? getRatingDescription(value) : value || 'N/A';

        return (
          <Table.Td key={item.destination} className={classes[`column${index}`]}>
            {displayValue}
          </Table.Td>
        );
      })}
    </Table.Tr>
  ));

  const selectHandler = ({ nutsId, nutsName, countryName }: Nuts) => {
    setSelectClicked(true);
    selectProcessor({ nutsId, nutsName, countryName });
  };

  return (
    <div>
      <Group bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={20} mt={0} justify="center">
        <Title order={3} c="white">
          Destination Region Comparison
        </Title>
      </Group>
      {data && (
        <Table
          borderColor="var(--mantine-color-dark-2)"
          highlightOnHoverColor="var(--mantine-color-dark-1)"
          highlightOnHover
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>REGION</Table.Th>
              {nutDestinations.map((destination, index) => (
                <Table.Th key={destination.nutsId} className={classes[`column${index}`]} ta="center">
                  <Stack>
                    {destination ? `${destination.nutsName}, ${destination.countryName}` : 'N/A'}
                    <Button
                      size="xs"
                      variant="filled"
                      color="green"
                      disabled={selectClicked}
                      onClick={() => selectHandler({ ...destination })}
                    >
                      Select and Continue
                    </Button>
                  </Stack>
                </Table.Th>
              ))}{' '}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      )}
    </div>
  );
}

export default CompareTable;
